import type { ButtonHTMLAttributes } from 'react';

type Variant = 'primary' | 'secondary' | 'danger' | 'warning' | 'info' | 'success';
type Size = 'sm' | 'xs';

interface ButtonProps {
  variant: Variant;
  outlined?: boolean;
  ghost?: boolean;
  size?: Size;
  disabled?: boolean;
  onClick?: () => void;
  type?: ButtonHTMLAttributes<HTMLButtonElement>['type'];
  IconComponentLeft?: React.FC<React.SVGProps<SVGSVGElement>>;
  IconComponentRight?: React.FC<React.SVGProps<SVGSVGElement>>;
  className?: string;
  isProcessing?: boolean;
}

const variantClassNames: Record<Variant, string> = {
  primary: 'btn-primary',
  secondary: 'btn-secondary',
  danger: 'btn-danger',
  warning: 'btn-warning',
  info: 'btn-info',
  success: 'btn-success',
};

const outlinedVariantClassNames: Record<Variant, string> = {
  primary: 'btn-outline-primary',
  secondary: 'btn-outline-secondary',
  danger: 'btn-outline-danger',
  warning: 'btn-outline-warning',
  info: 'btn-outline-info',
  success: 'btn-outline-success',
};

const ghostVariantClassNames: Record<Variant, string> = {
  primary: 'btn-ghost-primary',
  secondary: 'btn-ghost-secondary',
  danger: 'btn-ghost-danger',
  warning: 'btn-ghost-warning',
  info: 'btn-ghost-info',
  success: 'btn-ghost-success',
};

const sizeClassNames: Record<Size, string> = {
  sm: 'btn-sm',
  xs: 'btn-xs',
};

export const Button: FCC<ButtonProps> = ({
  variant,
  outlined,
  ghost,
  size,
  disabled,
  onClick,
  children,
  type,
  IconComponentLeft,
  IconComponentRight,
  className,
  isProcessing = false,
  ...dataAttributes
}) => {
  const variantClass = ghost
    ? ghostVariantClassNames[variant]
    : outlined
      ? outlinedVariantClassNames[variant]
      : variantClassNames[variant];

  return (
    <button
      {...dataAttributes}
      disabled={disabled}
      onClick={onClick}
      type={type ?? 'button'}
      className={`btn ${variantClass} ${size ? sizeClassNames[size] : ''} ${className ?? ''} ${isProcessing ? 'btn-state-processing' : ''} ${disabled ? 'cursor-not-allowed' : ''}`}
    >
      <span className={(!isProcessing ? '' : 'invisible ') + 'btn-content'}>
        {IconComponentLeft && <IconComponentLeft className='mr-2' />}
        {children}
        {IconComponentRight && <IconComponentRight className='ml-2' />}
      </span>

      <span
        className={
          (!isProcessing ? '' : 'invisible ') + !outlined
            ? 'btn-outline-processing-content'
            : 'btn-processing-content'
        }
      />
    </button>
  );
};
